<template>
  <div
    ref="componentRef"
    class="editable-component relative b-rounded-2"
    :class="{
      'h-full': isScribbleChild,
      'opacity-50': currentDraggedComponentId === id,
      'editable-component--edit': active,
      'editable-component--selected': selected,
      'editable-component--inline-edit': inlineEdit && !selected && !active,
      'editable-component--expired': expiredContent && !selected && !active,
      'editable-component--restricted': restrictedContent && !selected && !active,
      'editable-component--hide-outline': hideOutline,
    }"
    :style="editableComponentStyle"
    @mousedown.stop="onMouseDownOrTouchStart"
  >
    <CContentError
      v-if="expiredContent || restrictedContent"
      class="absolute top-6 left-1/2 -translate-x-1/2 z-1"
      v-bind="{
        restricted: restrictedContent,
        expired: expiredContent,
      }"
      only-tag
      show-hover
    />

    <ResizeHandles
      v-if="resizable && selected"
      :handles="resizeHandles"
      :maintain-aspect-ratio="resizeMaintainAspectRatio"
      :model-value="componentNode?.style || {}"
      @resize-end="handleResizeEnd"
      @resize-start="handleResizeStart"
      @update:model-value="updateDimensions"
    />
    <div ref="topHandlerRef" class="h-0">
      <EditableComponentActionToolbar
        v-bind="popoverProps"
        :parent-type="parentType"
        :placement="isScribbleChild ? 'top-start' : 'top'"
        :show="!bottomHandlerVisible && !showOnlyBottomToolbar"
        @add="emit('add')"
        @asset-change="emit('asset-change', $event)"
        @bg-property-change="handleBackgroundPropertyChange"
        @content-change="emit('content-change', $event)"
        @discard="emit('discard')"
        @duplicate="emit('duplicate')"
        @edit="handleEdit"
        @remove="emit('remove')"
        @reset-spacing="resetSpacing"
        @set-palette-index="setPaletteIndex"
        @settings="emit('settings')"
        @start-drag="handleStartDrag"
        @toggle-collapse="isCollapsed = !isCollapsed"
        @toggle-inspect-node="isInspectingNode = !isInspectingNode"
        @toggle-theme-options="showThemeOptions = !showThemeOptions"
        @update-border-radius="updateBorderRadius"
        @update-component-node="updateComponentNode"
        @update-height="updateHeight"
        @update-margin-bottom="updateMarginBottom"
        @update-padding="updatePadding"
        @variant-change="emit('variant-change', $event)"
      >
        <template #toolbar-slot>
          <slot name="toolbar-slot" />
        </template>
      </EditableComponentActionToolbar>
    </div>
    <div v-if="bottomHandlerVisible || showOnlyBottomToolbar" :key="bottomToolbarKey" class="h-0">
      <EditableComponentActionToolbar
        v-bind="popoverProps"
        :placement="bottomToolbarPlacement"
        :show="bottomHandlerVisible || showOnlyBottomToolbar"
        @add="emit('add')"
        @asset-change="emit('asset-change', $event)"
        @bg-property-change="handleBackgroundPropertyChange"
        @content-change="emit('content-change', $event)"
        @discard="emit('discard')"
        @duplicate="emit('duplicate')"
        @edit="handleEdit"
        @remove="emit('remove')"
        @reset-spacing="resetSpacing"
        @set-palette-index="setPaletteIndex"
        @settings="emit('settings')"
        @start-drag="handleStartDrag"
        @toggle-collapse="isCollapsed = !isCollapsed"
        @toggle-inspect-node="isInspectingNode = !isInspectingNode"
        @toggle-theme-options="showThemeOptions = !showThemeOptions"
        @update-border-radius="updateBorderRadius"
        @update-component-node="updateComponentNode"
        @update-height="updateHeight"
        @update-margin-bottom="updateMarginBottom"
        @update-padding="updatePadding"
        @variant-change="emit('variant-change', $event)"
      >
        <template #toolbar-slot>
          <slot name="toolbar-slot" />
        </template>
      </EditableComponentActionToolbar>
    </div>
    <!-- Drop Zone -->
    <div
      v-if="isDraggingNode && currentDraggedOverComponentId === id && currentDraggedComponentId !== id"
      :class="[
        'drop-indicator absolute bg-transparent z-10',
        { 'top-0 w-full h-2': highlightedZone === 'top' },
        { 'bottom-0 w-full h-2': highlightedZone === 'bottom' },
        { 'left-0 w-2 h-full': highlightedZone === 'left' },
        { 'right-0 w-2 h-full': highlightedZone === 'right' },
        { 'bg-primary': highlightedZone },
      ]"
      @dragover.prevent
    />
    <div v-show="!isCollapsed" :class="{ 'h-full drag-handle cursor-grab': isScribbleChild }">
      <slot />
    </div>
    <div
      v-if="isCollapsed && componentNode"
      class="pa-4 w-full text-center bg-base rounded-2 case-upper color-text3 font-500 cursor-pointer"
    >
      {{
        componentNode.tracking_id !== componentNode.type
          ? componentNode.tracking_id
          : getComponentTitle(componentNode.type, t)
      }}
    </div>
    <CJsonEditor
      v-if="isInspectingNode && componentNode"
      :object="componentNode"
      @close="isInspectingNode = false"
      @update="updateComponentNode"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject, onMounted, onUnmounted, type CSSProperties, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DropdownOption } from 'naive-ui'

import EditableComponentActionToolbar from './EditableComponentActionToolbar.vue'
import CJsonEditor from '@lib/components/CJsonEditor/CJsonEditor.vue'
import ResizeHandles, { type ResizeHandlePosition } from './ResizeHandles.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { useCanvasDnd } from '@canvas-builder/composables/useCanvasDnd'
import {
  type ThemeComponentOptionEnum,
  type ThemePadding,
  type ThemeBorderRadius,
} from '@canvas-builder/composables/useCanvasTheme'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import useThemeVars from '@lib/composables/useThemeVars'
import { ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'

import { type LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { getComponentTitle } from '@canvas-builder/util/canvas.util'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    discard?: boolean // allow discard mode
    duplicate?: boolean // allow duplicate mode
    edit?: boolean // allow edit mode
    remove?: boolean // allow remove mode
    settings?: boolean // allow settings mode
    stylable?: boolean // allow style mode
    collapsible?: boolean // allow collapsible mode
    immovable?: boolean // allow collapsible mode
    excludeStylables?: ThemeComponentOptionEnum[] // a list of theming options that should not be visible
    add?: boolean // allow add mode
    active?: boolean // set as active editing mode
    selected?: boolean // set as selected component
    inlineEdit?: boolean // mark as inline editing mode e.g. text -- for now only styling changes
    hideOutline?: boolean // hide outline
    addIcon?: string // add icon
    editIcon?: string // edit icon
    discardIcon?: string // discard icon
    duplicateIcon?: string // duplicate icon
    parentType?: ComponentTypes // parent type
    settingsIcon?: string // settings icon
    removeIcon?: string // remove icon
    resizable?: boolean // allow resizable mode
    resizeHandles?: ResizeHandlePosition[] // list of handles to show
    resizeMaintainAspectRatio?: boolean
    variants?: {
      title: string
      options: DropdownOption[]
      selected?: DropdownOption['key']
    } // variants for the dropdown
    showOnlyTopToolbar?: boolean // force toolbar to always show at top
    to?: HTMLElement | string
    showOnlyBottomToolbar?: boolean // force toolbar to always show at bottom
    expiredContent?: boolean
    restrictedContent?: boolean
  }>(),
  {
    collapsible: true,
    showOnlyTopToolbar: false,
    showOnlyBottomToolbar: false,
    resizable: false,
    resizeHandles: () => [],
    resizeMaintainAspectRatio: false,
  },
)
const emit = defineEmits<{
  discard: []
  duplicate: []
  edit: []
  remove: []
  add: []
  select: []
  settings: []
  style: [v: CSSProperties]
  drop: [{ draggedId: string; position: 'top' | 'bottom' | 'left' | 'right' }]
  'variant-change': [variant: DropdownOption['key']]
  'asset-change': [{ assetId: string }]
  'content-change': [{ fileId: string; pageIndex?: number; pageId?: string; thumbnailUrl?: string }]
  'resize-start': []
  'resize-end': []
}>()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()

const bottomHandlerVisible = ref(false)
const componentRef = ref<HTMLElement | null>(null)
const topHandlerRef = ref<HTMLElement | null>(null)

const isInspectingNode = ref(false)
const showThemeOptions = ref(false)
const isCollapsed = ref(false)
const debuggable = computed(() => launchDarkly.value.enable_canvas_node_debugging)
const draggable = computed(() => !props.immovable && launchDarkly.value.enable_experimental_canvas_builder_dnd)
const { componentNodesById, patchNodeById } = useCanvas()
const componentNode = computed(() => (props.id ? componentNodesById.value[props.id] : null) as ComponentNode)
const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})
const showActionSeparator = computed(
  () => (props.remove || props.collapsible) && (props.add || props.edit || props.settings || props.duplicate),
)

const bottomToolbarPlacement = computed(() => {
  // When showing only bottom toolbar for scribble children, use left-aligned placement
  if (props.showOnlyBottomToolbar && isScribbleChild.value) {
    return 'bottom-start'
  }
  return 'bottom'
})

const bottomToolbarKey = computed(() => {
  // Create a reactive key that changes when component position changes
  // This forces the bottom toolbar to re-render and reposition when the component moves
  if (isScribbleChild.value && (bottomHandlerVisible.value || props.showOnlyBottomToolbar)) {
    const left = componentNode.value?.style?.left || '0'
    const top = componentNode.value?.style?.top || '0'
    return `bottom-toolbar-${props.id}-${left}-${top}`
  }
  return `bottom-toolbar-${props.id}`
})

const themeVars = useThemeVars()
const { borderRadiusThemeValues, componentSpacingThemeValues, createStyleUpdate, paddingThemeValues } =
  useComponentStyle(componentNode)

const {
  isDraggingNode,
  highlightedZone,
  currentDraggedOverComponentId,
  currentDraggedComponentId,
  // onDragOver,
  // onDragStart,
  // onDragEnd,
  setStartedFromHandle,
} = useCanvasDnd(props.id)

const popoverProps = computed(() => ({
  to: props.to,
  selected: props.selected,
  draggable: !!draggable.value,
  add: props.add,
  edit: props.edit,
  settings: props.settings,
  stylable: props.stylable,
  duplicate: props.duplicate,
  discard: props.discard,
  debuggable: !!debuggable.value,
  remove: props.remove,
  collapsible: props.collapsible,
  isCollapsed: isCollapsed.value,
  isInspectingNode: isInspectingNode.value,
  addIcon: props.addIcon,
  editIcon: props.editIcon,
  settingsIcon: props.settingsIcon,
  duplicateIcon: props.duplicateIcon,
  discardIcon: props.discardIcon,
  removeIcon: props.removeIcon,
  variants: props.variants,
  componentNode: componentNode.value,
  excludeStylables: props.excludeStylables,
  padding: paddingThemeValues.value,
  spacing: componentSpacingThemeValues.value,
  borderRadius: borderRadiusThemeValues.value,
  showActionSeparator: showActionSeparator.value,
}))

const editableComponentStyle = computed(() => {
  if (!isScribbleChild.value) return {}

  // recalculate by removing the borders' 2px
  return {
    width: componentNode.value?.style?.width ? `${parseInt(componentNode.value.style.width) + 2}px` : undefined,
    height: componentNode.value?.style?.height ? `${parseInt(componentNode.value.style.height) + 2}px` : undefined,
  }
})

function handleEdit() {
  if (isCollapsed.value) isCollapsed.value = false
  emit('edit')
}

function setPaletteIndex(index: number | undefined) {
  updateComponentNode({ theme_meta: { ...componentNode.value?.theme_meta, bg_color_palette_index: index } })
}

function onMouseDownOrTouchStart(e: MouseEvent | TouchEvent) {
  if ('button' in e && e.button !== 0) return
  emit('select')
}

function updateDimensions(value: Record<string, any>) {
  emit('style', createStyleUpdate(value))
}

function handleResizeStart() {
  emit('resize-start')
}

function handleResizeEnd() {
  emit('resize-end')
}

function handleStartDrag() {
  setStartedFromHandle(true)
}

function updateHeight(height?: string | null) {
  emit('style', createStyleUpdate({ height }))
}

function updatePadding(value: ThemePadding) {
  emit('style', createStyleUpdate({ padding: value }))
}

function updateMarginBottom(value: number | null) {
  emit('style', createStyleUpdate({ marginBottom: value === null ? null : `${value}px` }))
}

function updateBorderRadius(value: ThemeBorderRadius) {
  emit('style', createStyleUpdate({ borderRadius: value }))
}

function updateComponentNode(updatedNode: Record<string, any>) {
  const nodePatch = updatedNode as ComponentNode
  try {
    patchNodeById(props.id, nodePatch)
  } catch (error) {
    alert('The JSON is invalid 🤦. Please correct it and try again.')
  }
}

function resetSpacing() {
  emit('style', createStyleUpdate({ marginBottom: null }))
}

function handleBackgroundPropertyChange(property: string, value: string) {
  if (property === 'backgroundImage' && value === null) {
    emit(
      'style',
      createStyleUpdate({
        backgroundImage: null,
        backgroundPositionX: undefined,
        backgroundPositionY: undefined,
        backgroundRepeat: undefined,
        backgroundSize: undefined,
      }),
    )
  } else {
    emit('style', createStyleUpdate({ [property]: value }))
  }
}

let observer: IntersectionObserver | null = null

onMounted(() => {
  if (componentRef.value && topHandlerRef.value && !props.showOnlyTopToolbar && !props.showOnlyBottomToolbar) {
    observer = new IntersectionObserver(
      ([entry]) => {
        bottomHandlerVisible.value = !entry.isIntersecting
      },
      {
        threshold: 0,
        rootMargin: '-50px 0px 0px 0px',
      },
    )
    observer.observe(topHandlerRef.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style lang="scss" scoped>
.editable-component {
  border: 1px solid v-bind('themeVars.primary5');

  &--selected {
    border: 1px solid v-bind('themeVars.info');
  }

  &--inline-edit {
    border: 1px solid v-bind('themeVars.primary5');
  }

  &--expired {
    border: 1px solid #bc1637;
  }

  &--restricted {
    border: 1px solid #e5c302;
  }

  &--edit {
    border: 1px solid v-bind('themeVars.error');
  }

  &--hide-outline {
    border: 1px solid transparent;
  }
}

.editable-component__actions {
  box-shadow: 0px 6px 8px rgba(41, 51, 92, 0.13);
  border: 1px solid v-bind('themeVars.primary5');
}
</style>
